/**
 * Factory presets for FMSynthNode (Tone.js FM Synthesis)
 */

export interface FMSynthPreset {
  name: string;
  description: string;
  params: {
    harmonicity: number;
    modulationIndex: number;
    attack: number;
    decay: number;
    sustain: number;
    release: number;
    volume?: number;
  };
}

export const FM_SYNTH_PRESETS: FMSynthPreset[] = [
  // ─────────────────────────────────────────────────────────────
  // Classic FM Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Electric Piano',
    description: 'Classic DX7-style electric piano with bell-like attack',
    params: {
      harmonicity: 3,
      modulationIndex: 14,
      attack: 0.01,
      decay: 0.8,
      sustain: 0.3,
      release: 1.2
    }
  },
  {
    name: 'FM Bass',
    description: 'Punchy FM bass with harmonic richness',
    params: {
      harmonicity: 1,
      modulationIndex: 12,
      attack: 0.001,
      decay: 0.3,
      sustain: 0.4,
      release: 0.3
    }
  },
  {
    name: 'Crystal Bell',
    description: 'Pure bell tones with long decay',
    params: {
      harmonicity: 5,
      modulationIndex: 8,
      attack: 0.001,
      decay: 2.0,
      sustain: 0.0,
      release: 3.0
    }
  },
  {
    name: 'Brass Stab',
    description: 'Bright, brassy FM sound for stabs',
    params: {
      harmonicity: 2,
      modulationIndex: 20,
      attack: 0.02,
      decay: 0.2,
      sustain: 0.7,
      release: 0.3
    }
  },
  {
    name: 'Organ',
    description: 'Full-bodied FM organ with harmonic overtones',
    params: {
      harmonicity: 2,
      modulationIndex: 6,
      attack: 0.01,
      decay: 0.1,
      sustain: 0.9,
      release: 0.3
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Metallic / Inharmonic
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Marimba',
    description: 'Wooden mallet percussion with quick decay',
    params: {
      harmonicity: 4,
      modulationIndex: 10,
      attack: 0.001,
      decay: 0.5,
      sustain: 0.0,
      release: 0.5
    }
  },
  {
    name: 'Metallic',
    description: 'Inharmonic metallic tones, gong-like',
    params: {
      harmonicity: 1.414,
      modulationIndex: 25,
      attack: 0.001,
      decay: 1.5,
      sustain: 0.1,
      release: 2.0
    }
  },
  {
    name: 'Chime',
    description: 'Tubular bell chime, bright and resonant',
    params: {
      harmonicity: 3.5,
      modulationIndex: 15,
      attack: 0.001,
      decay: 2.5,
      sustain: 0.0,
      release: 3.0
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Leads & Pads
  // ─────────────────────────────────────────────────────────────
  {
    name: 'FM Lead',
    description: 'Cutting lead sound with FM brightness',
    params: {
      harmonicity: 3,
      modulationIndex: 18,
      attack: 0.01,
      decay: 0.2,
      sustain: 0.7,
      release: 0.4
    }
  },
  {
    name: 'Soft Lead',
    description: 'Gentle FM lead with lower modulation',
    params: {
      harmonicity: 2,
      modulationIndex: 5,
      attack: 0.05,
      decay: 0.3,
      sustain: 0.6,
      release: 0.5
    }
  },
  {
    name: 'FM Pad',
    description: 'Evolving FM pad with slow attack',
    params: {
      harmonicity: 2,
      modulationIndex: 8,
      attack: 0.8,
      decay: 0.5,
      sustain: 0.7,
      release: 2.0
    }
  },
  {
    name: 'Glass Pad',
    description: 'Crystalline FM pad with high harmonicity',
    params: {
      harmonicity: 5,
      modulationIndex: 4,
      attack: 1.0,
      decay: 0.5,
      sustain: 0.6,
      release: 2.5
    }
  }
];

export function getFMSynthPreset(name: string): FMSynthPreset | undefined {
  return FM_SYNTH_PRESETS.find(p => p.name === name);
}

export function getFMSynthPresetNames(): string[] {
  return FM_SYNTH_PRESETS.map(p => p.name);
}
